/**
 * Represents a parsed version with major, minor, build, and revision components.
 * Follows C#'s System.Version format: major.minor.build.revision
 */
export interface AppVersion {
  major: number;
  minor: number;
  build: number;
  revision: number;
}

/**
 * Parses a version string into an AppVersion object.
 * Accepts 2-4 part version strings (e.g. "1.2", "1.2.3", "1.2.3.4").
 * Missing components default to 0.
 * @returns The parsed AppVersion, or null if the string is not a valid version.
 */
export function parseVersion(version: string | null | undefined): AppVersion | null {
  if (!version) {
    return null;
  }

  const parts = version.split('.');
  if (parts.length < 2 || parts.length > 4) {
    return null;
  }

  const nums = parts.map(Number);
  if (nums.some((n) => isNaN(n) || n < 0)) {
    return null;
  }

  return {
    major: nums[0],
    minor: nums[1],
    build: nums[2] ?? 0,
    revision: nums[3] ?? 0,
  };
}

/**
 * Compares two version strings and returns whether the agent version is outdated.
 * Compares all 4 components in order: major, minor, build, revision.
 * @param agentVersion - The version reported by the agent (e.g. "0.1.1.0")
 * @param latestVersion - The latest published version (e.g. "0.1.1.1")
 * @returns true if the agent version is older than the latest version
 */
export function isVersionOutdated(
  agentVersion: string | null | undefined,
  latestVersion: string | null | undefined
): boolean {
  const agent = parseVersion(agentVersion);
  const latest = parseVersion(latestVersion);

  if (!agent || !latest) {
    return false;
  }

  if (agent.major !== latest.major) {
    return agent.major < latest.major;
  }
  if (agent.minor !== latest.minor) {
    return agent.minor < latest.minor;
  }
  if (agent.build !== latest.build) {
    return agent.build < latest.build;
  }
  return agent.revision < latest.revision;
}
